/** @jsx jsx */
import React, { useState } from 'react'
import { jsx } from "theme-ui"
import Link from "@components/Link"
import ButtonLink from "@components/ButtonLink"
import PropTypes from "prop-types"
import { Themed } from "@theme-ui/mdx"
import { BodyLarge } from "@components/primitives/Text"
import Button from "@components/Button"
import { rem } from "@src/theme"
import HubspotDownloadButton from "@modules/HubspotDownloadButton"
import Spacing from "@components/Spacers"
import CopyIcon from "@components/assets/svg/copy.svg"
import CheckIcon from "@components/assets/svg/check.svg"
import MailchimpDownloadButton from '@modules/MailchimpDownloadButton'

const CTAHeadingBlock = ({
  heading,
  body,
  buttonLabel,
  buttonLink,
  buttonExternal = false,
  buttonBg = "darkPurple",
  buttonColor = "white",
  buttonHoverStyles,
  linkHoverStyles,
  onButtonClick,
  links = [],
  linksInline = false,
  smallHeading,
  headingLevel = '1',
  widthLimit,
  downloadResource,
  downloadLabel,
  downloadTag,
  bodyColor,
  styles = {},
  children,
  copyLink
}) => {
  const [isCopied, setIsCopied] = useState(false)

  const handleCopyLink = () => {
    if (typeof window !== "undefined" && copyLink) {
      const url = window.location.href.split('#')[0] + '#' + copyLink
      navigator.clipboard.writeText(url)
        .then(() => {
          setIsCopied(true)
          setTimeout(() => {
            setIsCopied(false)
          }, 2000)
        })
        .catch(err => {
          console.error('Failed to copy: ', err)
        })
    }
  }

  const HeadingComp = Themed[`h${headingLevel}`]

  return (
    <div
      sx={{
        maxWidth: widthLimit ? rem(750) : "initial",
        ...styles,
      }}
    >
      <Spacing.Medium />
      {children}
      {heading && (
        smallHeading ? (
          <Themed.h3
            sx={{
              m: 0,
            }}
          >
            {heading}
          </Themed.h3>
        ) : (
          <HeadingComp
            sx={{
              color: 'inherit',
              m: 0,
            }}
          >
            {heading}
          </HeadingComp>
        )
      )}
      {body && (
        <>
          <Spacing.XSmall />
          <BodyLarge
            style={{
              my: 0,
              color: bodyColor,
            }}
          >
            {body}
          </BodyLarge>
        </>
      )}

      {buttonLabel || linksInline || downloadResource || copyLink ? <Spacing.Small /> : null}

      {downloadResource ? (
        <HubspotDownloadButton
          label={downloadLabel}
          hubspotTag={downloadTag}
        />
      ) : copyLink && !buttonLink && !onButtonClick ? (
        <div
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Button
            onClick={handleCopyLink}
            bg={buttonBg}
            color={buttonColor}
            hoverStyles={buttonHoverStyles}
          >
            {isCopied ? <CheckIcon sx={{ mb: '-2px', mr: 1, height: '1em' }} /> : <CopyIcon sx={{ mb: '-2px', mr: 1, height: '1em' }} />}
            {isCopied ? "Copied!" : buttonLabel || "Copy Link"}
          </Button>
        </div>
      ) : (buttonLabel && onButtonClick) ||
        (buttonLabel && buttonLink) ||
        (linksInline && links.length === 1) ? (
        <div
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {buttonLabel && buttonLink && (
            <ButtonLink
              to={buttonLink}
              className="unstyled-link"
              bg={buttonBg}
              color={buttonColor}
              hoverStyles={buttonHoverStyles}
              external={buttonExternal}
            >
              {buttonLabel}
            </ButtonLink>
          )}

          {buttonLabel && onButtonClick && (
            <Button
              onClick={onButtonClick}
              bg={buttonBg}
              color={buttonColor}
              hoverStyles={buttonHoverStyles}
              sx={{ mr: 4 }}
            >
              {buttonLabel}
            </Button>
          )}

          {linksInline && links.length > 0 && (
            <Link
              to={links[0].href}
              link={links[0].href}
              style={{
                ml: buttonLabel ? 3 : 0,
                fontSize: rem(18)
              }}
              hoverStyles={linkHoverStyles}
              external={links[0].external}
            >
              {links[0].label}
            </Link>
          )}
        </div>
      ) : null}
      {(!linksInline && links.length > 0) && (
        <>
          <Spacing.Medium />
          <div sx={{ flex: '1' }} />
          <div sx={{ display: 'flex', columnGap: 6, rowGap: 4, alignItems: 'end', flexWrap: 'wrap' }}>
            {links.map((link) => (
              <Link
                to={link.href}
                link={link.href}
                style={{ fontSize: rem(18) }}
                hoverStyles={linkHoverStyles}
                external={link.external}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </>
      )}
      <Spacing.Medium />
    </div>
  )
}

CTAHeadingBlock.propTypes = {
  heading: PropTypes.string,
  body: PropTypes.string,
  buttonLabel: PropTypes.string,
  buttonLink: PropTypes.string,
  onButtonClick: PropTypes.func,
  buttonExternal: PropTypes.bool,
  linkLabel: PropTypes.string,
  linkTo: PropTypes.string,
  copyLink: PropTypes.string,
}

export default CTAHeadingBlock
